import type { EmailActivity } from '@common/types/activities';
import type { IntakeSummaryPdfData } from '@common/types/formSubmissions';
import type {
    ClientDocumentEntry,
    CreateProspectManually,
    ProspectEntry,
    ProspectFormResponse,
    ProspectResponse,
} from '@common/types/prospects';
import type { Operation } from 'fast-json-patch';
import axiosInstance from '@/services/axiosInstance';

export async function getProspectById(prospectId: string, prospectVersionId?: number | null) {
    const params = prospectVersionId ? { prospectVersionId } : null;
    return axiosInstance.get<ProspectResponse>(`api/v1/prospects/${prospectId}`, { params });
}

export async function getAllProspects(lawFirmId?: number) {
    return axiosInstance.get<ProspectEntry[]>('api/v1/prospects', {
        params: { lawFirmId },
    });
}

export async function updateProspectAssignment(
    prospectId: string,
    payload: { quoteApproveId: number; intakeCCEmails: string[] },
): Promise<ProspectResponse> {
    const response = await axiosInstance.patch(
        `api/v1/prospects/${prospectId}/assignment`,
        payload,
    );
    return response.data;
}

export async function patchProspectById(
    prospectId: string,
    payload: Operation[],
): Promise<ProspectResponse> {
    const response = await axiosInstance.patch(`api/v1/prospects/${prospectId}`, payload, {
        headers: { 'Content-Type': 'application/json-patch+json' },
    });
    return response.data;
}

export async function getQuoteFormResponse(prospectId: string) {
    return axiosInstance.get<ProspectFormResponse>(
        `api/v1/prospects/${prospectId}/quote-form-response`,
    );
}

export async function getIntakePreFillData(prospectId: string) {
    return axiosInstance.get<ProspectFormResponse>(
        `api/v1/prospects/${prospectId}/intake-pre-fill-data`,
    );
}

export async function getIntakeFormResponse(prospectId: string, prospectVersionId?: number | null) {
    const params = prospectVersionId ? { prospectVersionId } : null;
    return axiosInstance.get<ProspectFormResponse>(
        `api/v1/prospects/${prospectId}/intake-form-response`,
        { params },
    );
}

export async function downloadIntakeSummaryPdf(data: IntakeSummaryPdfData) {
    return axiosInstance.post<Blob>('api/v1/forms/intake-summary-pdf', data, {
        responseType: 'blob',
        headers: {
            Accept: 'application/pdf',
        },
    });
}

/** The sample V1 intake's summary PDF, for previewing the V1 layout */
export async function downloadSampleV1IntakeSummaryPdf(
    options: Pick<
        IntakeSummaryPdfData,
        'showEmptyAnswersInIntakeSummary' | 'showContactsAsColumnsInIntakeSummary'
    >,
) {
    return axiosInstance.get<Blob>('api/v1/forms/sample-v1-intake-summary-pdf', {
        params: options,
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
    });
}

export async function downloadV1IntakeSummaryPdf(
    prospectId: string,
    prospectVersionId?: number | null,
) {
    const params = prospectVersionId ? { prospectVersionId } : null;
    return axiosInstance.get<Blob>(`api/v1/prospects/${prospectId}/intake-summary-pdf`, {
        params,
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
    });
}

export async function getProspectClientDocumentsData(
    prospectId: string,
    prospectVersionId?: number | null,
) {
    const params = prospectVersionId ? { prospectVersionId } : null;
    return axiosInstance.get<ClientDocumentEntry[]>(
        `api/v1/prospects/${prospectId}/client-documents`,
        { params },
    );
}

export async function getProspectClientDocuments(
    prospectId: string,
    prospectVersionId?: number | null,
): Promise<ClientDocumentEntry[]> {
    const params = prospectVersionId ? { prospectVersionId } : null;
    const response = await axiosInstance.get<ClientDocumentEntry[]>(
        `api/v1/prospects/${prospectId}/client-documents`,
        { params },
    );
    return response.data;
}

export async function getAllEmailActivities(prospectId: string) {
    return axiosInstance.get<EmailActivity[]>(`api/v1/activities/prospects/${prospectId}`);
}

export async function sendQuoteManually(prospectId: string) {
    return axiosInstance.post(`api/v1/prospects/${prospectId}/sendQuote`);
}

export async function createProspectManually(payload: CreateProspectManually) {
    return axiosInstance.post('api/v1/prospects/manually', payload);
}

export async function deleteProspects(payload: string[]) {
    return axiosInstance.put('api/v1/prospects', payload);
}
